import * as React from 'react';
import { cn } from '@/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
  /** Enable hover glow effect and lift animation */
  interactive?: boolean;
}

function Card({ className, ref, interactive = false, ...props }: CardProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg bg-white shadow-md overflow-hidden',
        interactive && 'card-interactive',
        className
      )}
      {...props}
    />
  );
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
}

function CardHeader({ className, ref, ...props }: CardHeaderProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5 border-b border-gray-200',
        // Responsive padding: p-4 mobile, p-5 tablet, p-6 desktop
        'p-4 md:p-5 lg:p-6',
        className
      )}
      {...props}
    />
  );
}

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  ref?: React.Ref<HTMLHeadingElement>;
}

function CardTitle({ className, ref, ...props }: CardTitleProps) {
  return (
    <h3
      ref={ref}
      className={cn(
        'font-bold leading-none tracking-tight mb-0',
        // Responsive typography: text-xl mobile, text-2xl desktop
        'text-xl md:text-2xl',
        className
      )}
      {...props}
    />
  );
}

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  ref?: React.Ref<HTMLParagraphElement>;
}

function CardDescription({ className, ref, ...props }: CardDescriptionProps) {
  return (
    <p ref={ref} className={cn('text-sm text-gray-500 mb-0', className)} {...props} />
  );
}

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
}

function CardContent({ className, ref, ...props }: CardContentProps) {
  return (
    <div
      ref={ref}
      className={cn(
        // Responsive padding: p-4 mobile, p-5 tablet, p-6 desktop
        'p-4 md:p-5 lg:p-6',
        className
      )}
      {...props}
    />
  );
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
}

function CardFooter({ className, ref, ...props }: CardFooterProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center bg-gray-50 border-t border-gray-200',
        // Responsive padding
        'p-3 px-4 md:p-4 md:px-5 lg:px-6',
        className
      )}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
